import React, { useState, useEffect, useMemo, useRef, useCallback } from "react"
import { useParams, useNavigate, useLocation } from "react-router-dom"
import {
  Card,
  Spinner,
  Button,
  ButtonGroup,
  Modal,
  Form,
  Row,
  Col,
  Tooltip,
  OverlayTrigger
} from "react-bootstrap"
import Layout from "../components/layout/Layout"
import useAuthAxios from "../hooks/useAuthAxios"
import { useAuth } from "../context/AuthContext"
import { formatDateForDisplay, formatDateForApi } from "../utils/dateUtils"
import { toast } from "react-toastify"
import BackButton from "../components/common/BackButton"
import FileUploadModal from "../components/repairs/FileUploadModal"
import DocumentUploadCard from "../components/repairs/DocumentUploadCard"
import { useEExpert } from "../context/EExpertContext"
import JSZip from "jszip"
import { REPAIR_ACTIONS, REPAIR_PERMISSIONS, REPAIR_ROLES } from "../constants/repairPermissions"

const REGISTRATION_DOCUMENT_KIND_ID = 1
const INVOICE_KIND_IDS = [9, 17]
const FINANCIAL_OFFER_KIND_ID = 10
const ASSIGNMENT_REQUEST_KIND_ID = 11
const LOIPA_KIND_IDS = [12, 13, 14, 15]
const REPAIRED_VEHICLE_PHOTO_KIND_ID = 16

const RepairDetails = () => {
  const { repairId: urlRepairId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { get, put, del: deleteFile, post } = useAuthAxios()
  const { getAppLookupValue, appLookups, isAdmin, isGarage, isExpert, user } = useAuth()
  const { sessionId, setSessionId, setShowEExpert, showEExpert } = useEExpert()
  const [repairId, setRepairId] = useState(undefined)
  const [repair, setRepair] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState({ status: false, action: null, progress: null })
  const [showRejectionModal, setShowRejectionModal] = useState(false)
  const [showCancelAssignmentModal, setShowCancelAssignmentModal] = useState(false)
  const [showAssignmentModal, setShowAssignmentModal] = useState({ show: false, type: null })
  const [rejectionComments, setRejectionComments] = useState("")
  const [cancelAssignmentComments, setCancelAssignmentComments] = useState("")
  const [selectedSubStatus, setSelectedSubStatus] = useState("")
  const [selectedExpertId, setSelectedExpertId] = useState("")
  const [users, setUsers] = useState([])
  const [showDeleteFileModal, setShowDeleteFileModal] = useState(false)
  const [fileToDelete, setFileToDelete] = useState(null)
  const [showPreviewModal, setShowPreviewModal] = useState(false)
  const [previewFile, setPreviewFile] = useState(null)
  const [previewUrl, setPreviewUrl] = useState(null)
  const [showFileUpload, setShowFileUpload] = useState(false)
  const [downloadingFileId, setDownloadingFileId] = useState(null)
  const [uploadFileKindIds, setUploadFileKindIds] = useState([])
  const [previewIndex, setPreviewIndex] = useState(0)
  const [fileCache, setFileCache] = useState({})
  const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 })
  const [zoomLevel, setZoomLevel] = useState(1)
  const imageRef = useRef(null)
  const videoRef = useRef(null)
  const videoContainerRef = useRef(null)
  const [zoomMode, setZoomMode] = useState("none") // 'none', 'scale', 'lens'
  const [isNew, setIsNew] = useState(false)
  const [pageError, setPageError] = useState(null)
  const [showOfferActionModal, setShowOfferActionModal] = useState({ show: false, actionType: null })
  const [offerActionComments, setOfferActionComments] = useState("")
  const [showSubmitConfirmationModal, setShowSubmitConfirmationModal] = useState(false)
  const [submitComment, setSubmitComment] = useState("")
  const [newMessage, setNewMessage] = useState("")
  const [showNotificationModal, setShowNotificationModal] = useState(false)
  const [notificationPrefs, setNotificationPrefs] = useState({
    sms: false,
    email: false
  })
  const [notificationPhone, setNotificationPhone] = useState("")
  const [notificationEmail, setNotificationEmail] = useState("")
  const [isModalSubmitting, setIsModalSubmitting] = useState(false)

  const effectiveStatus = useMemo(() => {
    if (isNew) {
      return 10
    }
    return repair?.Status
  }, [isNew, repair?.Status])

  const hasPermission = useCallback(
    (action, statusOverride) => {
      const statusToCheck = statusOverride ?? effectiveStatus

      if (!statusToCheck) {
        return false
      }

      const statusPermissions = REPAIR_PERMISSIONS[statusToCheck]
      if (!statusPermissions) {
        return false
      }

      if (isGarage() && statusPermissions[REPAIR_ROLES.GARAGE]?.[action]) {
        return true
      }

      if (isExpert() && statusPermissions[REPAIR_ROLES.EXPERT]?.[action]) {
        return true
      }

      if (isAdmin() && statusPermissions[REPAIR_ROLES.ADMIN]?.[action]) {
        return true
      }

      return false
    },
    [effectiveStatus, isAdmin, isExpert, isGarage]
  )

  const canUploadFiles = hasPermission(REPAIR_ACTIONS.UPLOAD_FILES)
  const canDeleteFiles = hasPermission(REPAIR_ACTIONS.DELETE_FILES)
  const canUploadRepairedFiles = hasPermission(REPAIR_ACTIONS.UPLOAD_REPAIRED_FILES)
  const canDeleteRepairedFiles = hasPermission(REPAIR_ACTIONS.DELETE_REPAIRED_FILES)
  const canUploadRegistration = hasPermission(REPAIR_ACTIONS.UPLOAD_REGISTRATION)
  const canDeleteRegistration = hasPermission(REPAIR_ACTIONS.DELETE_REGISTRATION)
  const canUploadFinancialOffer = hasPermission(REPAIR_ACTIONS.UPLOAD_FINANCIAL_OFFER)
  const canDeleteFinancialOffer = hasPermission(REPAIR_ACTIONS.DELETE_FINANCIAL_OFFER)
  const canUploadAssignmentRequest = hasPermission(REPAIR_ACTIONS.UPLOAD_ASSIGNMENT_REQUEST)
  const canDeleteAssignmentRequest = hasPermission(REPAIR_ACTIONS.DELETE_ASSIGNMENT_REQUEST)
  const canUploadOtherDocuments = hasPermission(REPAIR_ACTIONS.UPLOAD_OTHER_DOCUMENTS)
  const canDeleteOtherDocuments = hasPermission(REPAIR_ACTIONS.DELETE_OTHER_DOCUMENTS)
  const canUploadInvoices = hasPermission(REPAIR_ACTIONS.UPLOAD_INVOICE)
  const canDeleteInvoices = hasPermission(REPAIR_ACTIONS.DELETE_INVOICE)
  const canEditVehicle = hasPermission(REPAIR_ACTIONS.EDIT_VEHICLE)
  const canEditInsured = hasPermission(REPAIR_ACTIONS.EDIT_INSURED)
  const canTriggerExpertCall = hasPermission(REPAIR_ACTIONS.EXPERT_CALL)
  const canOpenRepair = hasPermission(REPAIR_ACTIONS.OPEN_REPAIR)
  const canCancelExpertDecision = hasPermission(REPAIR_ACTIONS.CANCEL_EXPERT_DECISION)

  const getMissingDataTooltip = (completion = false) => {
    if (!completion) {
      const missing = []
      if (registrationDocuments.length === 0) missing.push("Άδεια Κυκλοφορίας")
      if (financialOffers.length === 0) missing.push("Οικονομική Προσφορά")
      if (photoFiles.length === 0) missing.push("Φωτογραφίες")
      if (!repair?.Vehicle?.Make) missing.push("Μάρκα")
      if (!repair?.Vehicle?.Model) missing.push("Μοντέλο")
      if (!repair?.Insured?.Surname) missing.push("Επώνυμο")
      if (!repair?.Insured?.Name) missing.push("Όνομα")
      if (!repair?.Insured?.Mobile) missing.push("Κινητό")
      if (!repair?.Insured?.Phone) missing.push("Τηλ. Επικοινωνίας")
      if (!repair?.Insured?.TaxNumber) missing.push("Α.Φ.Μ.")

      if (missing.length === 0) {
        return <></>
      }

      return (
        <Tooltip id="button-tooltip">
          {"Απαιτείται η συμπλήρωση των παρακάτω δεδομένων: " + missing.join(", ")}
        </Tooltip>
      )
    } else {
      // Define document requirements with their messages
      const documentRequirements = [
        {
          isMissing: invoices.length === 0,
          singleMessage: "Απαιτείται το ανέβασμα του τιμολογίου ή υπεύθυνης δήλωσης μικροζημιάς",
          combinedMessage: "το ανέβασμα του τιμολογίου ή υπεύθυνης δήλωσης μικροζημιάς"
        },
        {
          isMissing: repairedVehiclePhotoFiles.length === 0,
          singleMessage: "Απαιτείται το ανέβασμα τουλάχιστον μίας φωτογραφίας του επισκευασμένου οχήματος",
          combinedMessage: "τουλάχιστον μίας φωτογραφίας του επισκευασμένου οχήματος"
        },
        {
          isMissing: loipaDocuments.length === 0,
          singleMessage: "Απαιτείται το ανέβασμα τουλάχιστον ενός εγγράφου στα λοιπά δικαιολογητικά",
          combinedMessage: "τουλάχιστον ενός εγγράφου στα λοιπά δικαιολογητικά"
        }
      ]

      const missingDocuments = documentRequirements.filter(req => req.isMissing)

      let tooltipText = ""
      if (missingDocuments.length === 1) {
        tooltipText = missingDocuments[0].singleMessage
      } else if (missingDocuments.length > 1) {
        const combinedMessages = missingDocuments.map(req => req.combinedMessage)
        const lastMessage = combinedMessages.pop()
        const prefix = combinedMessages.length > 0 ? "Απαιτούνται " : "Απαιτείται "
        tooltipText = `${prefix}${
          combinedMessages.length > 0 ? combinedMessages.join(", ") + " και " : ""
        }${lastMessage}`
      }

      return tooltipText !== "" ? <Tooltip id="button-tooltip">{tooltipText}</Tooltip> : <></>
    }
  }

  const {
    photoFiles,
    repairedVehiclePhotoFiles,
    registrationDocuments,
    financialOffers,
    assignmentRequests,
    invoices,
    loipaDocuments
  } = useMemo(() => {
    const allFiles = repair?.RepairFilesList?.RepairFilesDetails.sort((a, b) => b.FileID - a.FileID) || []
    const documentFileKindIDs = [
      REGISTRATION_DOCUMENT_KIND_ID,
      ...INVOICE_KIND_IDS,
      FINANCIAL_OFFER_KIND_ID,
      ASSIGNMENT_REQUEST_KIND_ID,
      ...LOIPA_KIND_IDS,
      REPAIRED_VEHICLE_PHOTO_KIND_ID
    ]

    return {
      photoFiles: allFiles.filter(file => !documentFileKindIDs.includes(file.FileKindID)),
      repairedVehiclePhotoFiles: allFiles.filter(file => file.FileKindID === REPAIRED_VEHICLE_PHOTO_KIND_ID),
      registrationDocuments: allFiles.filter(file => file.FileKindID === REGISTRATION_DOCUMENT_KIND_ID),
      financialOffers: allFiles.filter(file => file.FileKindID === FINANCIAL_OFFER_KIND_ID),
      assignmentRequests: allFiles.filter(file => file.FileKindID === ASSIGNMENT_REQUEST_KIND_ID),
      invoices: allFiles.filter(file => INVOICE_KIND_IDS.includes(file.FileKindID)),
      loipaDocuments: allFiles.filter(file => LOIPA_KIND_IDS.includes(file.FileKindID))
    }
  }, [repair?.RepairFilesList])

  // Memoize lookup options - Move this before the validation effect
  const lookupOptions = useMemo(() => {
    const getLookupOptions = table => {
      return appLookups?.filter(lookup => lookup.Table === table) || []
    }

    return {
      fileKind: getLookupOptions("FilesKind"),
      subStatus: getLookupOptions("SubStatus"),
      companies: getLookupOptions("Companies")
    }
  }, [appLookups])

  // Fetch users for expert dropdown
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await get("/users", { params: { RoleID: 1, Active: 1 } }) // RoleID 1 is for experts, only active experts
        if (response.success && response.data?.Results?.APIuser) {
          setUsers(response.data.Results.APIuser)
        }
      } catch (err) {
        console.error("Failed to fetch users:", err)
      }
    }
    fetchUsers()
  }, [get])

  // Reset showEExpert on mount/reload
  useEffect(() => {
    setShowEExpert(false)
  }, [setShowEExpert]) // eslint-disable-line react-hooks/exhaustive-deps

  const refreshRepairDetails = useCallback(async () => {
    try {
      setLoading(true)
      const response = await get(`/repairs/${repairId}`)
      setRepair(response.data)
    } catch (error) {
      console.error("Error refreshing repair details:", error)
      toast.error("Αποτυχία ανανέωσης στοιχείων επισκευής")
    } finally {
      setLoading(false)
    }
  }, [get, repairId, setRepair, setLoading])

  useEffect(() => {
    if (urlRepairId === "new") {
      if (!location.state?.ClaimNo || !location.state?.InsuranceCompany || !location.state?.PlateNumber) {
        setPageError(
          "Δεν μπορείτε να δημιουργήσετε νέα επισκευή χωρίς αριθμό φακέλου, ασφαλιστική εταιρεία και αριθμό πινακίδας. Παρακαλώ ξεκινήστε από την αναζήτηση οχήματος."
        )
        setLoading(false)
        return
      }

      setIsNew(true)
      const newRepairData = {
        Vehicle: { PlateNumber: location.state.PlateNumber },
        Insured: {},
        ClaimNo: location.state.ClaimNo,
        InsuranceCompany: location.state.InsuranceCompany
      }

      setRepair(newRepairData)
      setRepairId(undefined)
      setLoading(false)
    } else if (urlRepairId) {
      setIsNew(false)
      setRepairId(urlRepairId)
    }
  }, [urlRepairId, setRepair, setIsNew, setRepairId, setLoading, location.state])

  useEffect(() => {
    if (repairId && !isNew) {
      refreshRepairDetails()
    }
  }, [repairId, isNew, refreshRepairDetails])

  useEffect(() => {
    if (location.state?.justCreated) {
      toast.info("Μπορείτε τώρα να μεταφορτώσετε αρχεία για τη νέα επισκευή.")
      // Remove the justCreated flag from the location state to prevent re-triggering
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location, navigate])

  const handleCancelRepair = async () => {
    setSelectedSubStatus("")
    setRejectionComments("")
    setShowRejectionModal(true)
  }

  const handleConfirmCancelRepair = async () => {
    if (!selectedSubStatus) {
      toast.error("Παρακαλώ επιλέξτε αιτία απόρριψης")
      return
    }

    try {
      setIsSubmitting({ status: true, action: "cancelRepair" })
      const response = await put(`/repairs/${repairId}`, {
        RepairID: repairId,
        Status: 60,
        SubStatusRejected: selectedSubStatus,
        Comments: rejectionComments,
        actionType: 90
      })

      // Check if the nested response indicates failure
      if (!response.data?.IsSuccess) {
        throw new Error(response.data?.Problem)
      }

      // Refresh the repair data after cancellation
      await refreshRepairDetails()
      setRejectionComments("")
      setSelectedSubStatus("")
      toast.success("Η επισκευή ακυρώθηκε επιτυχώς", { autoClose: 3000 })
    } catch (error) {
      console.error("Error canceling repair:", error)
      toast.error(error.message || "Αποτυχία ακύρωσης επισκευής")
    } finally {
      setIsSubmitting({ status: false, action: null })
      setShowRejectionModal(false)
    }
  }

  const handleCancelExpertDecision = async () => {
    try {
      setIsSubmitting({ status: true, action: "cancelExpertDecision" })
      const response = await put(`/repairs/${repairId}`, {
        RepairID: repairId,
        actionType: 124
      })

      if (!response.data?.IsSuccess) {
        throw new Error(response.data?.Problem)
      }

      await refreshRepairDetails()
      toast.success("Η απόφαση πραγματογνώμονα ακυρώθηκε επιτυχώς", { autoClose: 3000 })
    } catch (error) {
      console.error("Error canceling expert decision:", error)
      toast.error(error.message || "Αποτυχία ακύρωσης απόφασης πραγματογνώμονα")
    } finally {
      setIsSubmitting({ status: false, action: null })
    }
  }

  const handleRestore = async () => {
    try {
      setIsSubmitting({ status: true, action: "restore" })
      // Set status to 1 if expert is assigned, 0 if not
      const newStatus = repair?.ExpertID ? 1 : 0
      const response = await put(`/repairs/${repairId}`, {
        RepairID: repairId,
        Status: newStatus,
        actionType: 91
      })

      // Check if the nested response indicates failure
      if (!response.data?.IsSuccess) {
        throw new Error(response.data?.Problem)
      }

      // Refresh the repair data after restoration
      await refreshRepairDetails()
      toast.success("Η επισκευή επανήλθε επιτυχώς", { autoClose: 3000 })
    } catch (error) {
      console.error("Error restoring repair:", error)
      toast.error(error.message || "Αποτυχία επαναφοράς επισκευής")
    } finally {
      setIsSubmitting({ status: false, action: null })
    }
  }

  const handleReOpen = async () => {
    try {
      setIsSubmitting({ status: true, action: "reopen" })
      const response = await put(`/repairs/${repairId}`, {
        RepairID: repairId,
        Status: 95,
        actionType: 101
      })

      if (!response.data?.IsSuccess) {
        throw new Error(response.data?.Problem)
      }

      await refreshRepairDetails()
      toast.success("Η επισκευή ανοίχθηκε επιτυχώς", { autoClose: 3000 })
    } catch (error) {
      console.error("Error reopening repair:", error)
      toast.error(error.message || "Αποτυχία ανοίγματος επισκευής")
    } finally {
      setIsSubmitting({ status: false, action: null })
    }
  }

  const handleCancelAssignment = async () => {
    setSelectedSubStatus("")
    setCancelAssignmentComments("")
    setShowCancelAssignmentModal(true)
  }

  const handleConfirmCancelAssignment = async () => {
    if (!selectedSubStatus) {
      toast.error("Παρακαλώ επιλέξτε αιτία ακύρωσης")
      return
    }

    try {
      setIsSubmitting({ status: true, action: "cancelAssignment" })
      const response = await put(`/repairs/${repairId}`, {
        RepairID: repairId,
        SubStatusRejected: selectedSubStatus,
        Comments: cancelAssignmentComments,
        actionType: 23
      })

      // Check if the nested response indicates failure
      if (!response.data?.IsSuccess) {
        throw new Error(response.data?.Problem)
      }

      if (!isAdmin()) {
        // For non-admin users, navigate to home
        setShowCancelAssignmentModal(false)
        setCancelAssignmentComments("")
        setSelectedSubStatus("")
        toast.success("Η ανάθεση ακυρώθηκε επιτυχώς", { autoClose: 3000 })
        navigate("/")
      } else {
        // For admin users, refresh the repair data
        await refreshRepairDetails()
        setShowCancelAssignmentModal(false)
        setCancelAssignmentComments("")
        setSelectedSubStatus("")
        toast.success("Η ανάθεση ακυρώθηκε επιτυχώς", { autoClose: 3000 })
      }
    } catch (error) {
      console.error("Error canceling assignment:", error)
      toast.error(error.message || "Αποτυχία ακύρωσης ανάθεσης")
    } finally {
      setIsSubmitting({ status: false, action: null })
      setShowCancelAssignmentModal(false)
    }
  }

  const handleSave = async () => {
    try {
      setIsSubmitting({ status: true, action: "save" })
      // Create a copy of the repair object for sending
      const repairToSend = { ...repair }
      repairToSend.Vehicle = { ...repair?.Vehicle }

      // Remove properties that should not be sent
      delete repairToSend.RepairHistoryList
      delete repairToSend.RepairFilesList
      delete repairToSend.CustomerID
      delete repairToSend.IssueDateTime
      delete repairToSend.InsuranceCompanyID

      // Format dates in the required API format
      if (repairToSend.IssueDateTime) {
        repairToSend.IssueDateTime = formatDateForApi(repairToSend.IssueDateTime)
      }
      if (repairToSend.CompletionDateTime) {
        repairToSend.CompletionDateTime = formatDateForApi(repairToSend.CompletionDateTime)
      }

      // Convert from { short: [1,2,3] } to [{ short: 1 }, { short: 2 }, { short: 3 }]
      repairToSend.Vehicle.ProtectionMeasuresList = (repair?.Vehicle.ProtectionMeasuresList?.short || []).map(
        code => ({ short: code })
      )

      // Convert from { short: [1,2,3] } to [{ short: 1 }, { short: 2 }, { short: 3 }]
      repairToSend.Vehicle.ExtraEquipmentList = (repair?.Vehicle.ExtraEquipmentList?.short || []).map(
        code => ({ short: code })
      )

      // Convert from { short: [1,2,3] } to [{ short: 1 }, { short: 2 }, { short: 3 }]
      repairToSend.Vehicle.PreExistingDamagesList = (repair?.Vehicle.PreExistingDamagesList?.short || []).map(
        code => ({ short: code })
      )

      const response = await put(`/repairs/${repairId}`, repairToSend)

      // Check if the nested response indicates failure
      if (!response.data?.IsSuccess) {
        throw new Error(response.data?.Problem)
      }

      await refreshRepairDetails()
      toast.success("Οι αλλαγές αποθηκεύτηκαν επιτυχώς", { autoClose: 3000 })
    } catch (err) {
      toast.error(err.message || "Αποτυχία αποθήκευσης αλλαγών")
    } finally {
      setIsSubmitting({ status: false, action: null })
    }
  }

  const handleInputChange = (path, value) => {
    setRepair(prev => {
      const newRepair = { ...prev }
      const pathParts = path.split(".")
      let current = newRepair

      for (let i = 0; i < pathParts.length - 1; i++) {
        current = current[pathParts[i]]
      }

      current[pathParts[pathParts.length - 1]] = value
      return newRepair
    })
  }

  const handleAssignRepair = () => {
    setSelectedExpertId("")
    setShowAssignmentModal({ show: true, type: "new" })
  }

  const handleSubmitRepair = async () => {
    if (repair?.Status === 50) {
      setSubmitComment("")
      setShowSubmitConfirmationModal(true)
    } else {
      try {
        setIsSubmitting({ status: true, action: "submission" })

        const payload = {
          repairId: repairId,
          actionType: 15
        }

        const response = await put(`/repairs/${repairId}`, payload)

        if (!response.data?.IsSuccess) {
          throw new Error(response.data?.Problem)
        }

        await refreshRepairDetails()
        toast.success("Η υποβολή ολοκληρώθηκε επιτυχώς", { autoClose: 3000 })
      } catch (error) {
        console.error("Error submitting repair:", error)
        toast.error(error.message || "Αποτυχία υποβολής επισκευής")
      } finally {
        setIsSubmitting({ status: false, action: null })
      }
    }
  }

  const handleConfirmSubmitRepair = async () => {
    try {
      setIsSubmitting({ status: true, action: "submission" })

      const payload = {
        repairId: repairId,
        Status: 40,
        Comments: submitComment
      }

      const response = await put(`/repairs/${repairId}`, payload)

      if (!response.data?.IsSuccess) {
        throw new Error(response.data?.Problem)
      }

      await refreshRepairDetails()
      toast.success("Η υποβολή ολοκληρώθηκε επιτυχώς", { autoClose: 3000 })
    } catch (error) {
      console.error("Error submitting repair:", error)
      toast.error(error.message || "Αποτυχία υποβολής επισκευής")
    } finally {
      setIsSubmitting({ status: false, action: null })
      setShowSubmitConfirmationModal(false)
    }
  }

  const handleChangeAssignment = () => {
    setSelectedExpertId(repair?.ExpertID?.toString() || "")
    setShowAssignmentModal({ show: true, type: "change" })
  }

  const handleConfirmAssignment = async () => {
    if (!selectedExpertId) {
      toast.error("Παρακαλώ επιλέξτε πραγματογνώμονα")
      return
    }

    // For change assignment, validate that a different expert is selected
    if (repair?.Status === 1 && selectedExpertId === repair?.ExpertID?.toString()) {
      toast.error("Παρακαλώ επιλέξτε διαφορετικό πραγματογνώμονα")
      return
    }

    try {
      setIsSubmitting({ status: true, action: "assignment" })

      const payload = {
        repairId: repairId,
        ExpertID: parseInt(selectedExpertId, 10),
        actionType: repair?.Status === 1 ? 22 : 21
      }

      const response = await put(`/repairs/${repairId}`, payload)

      // Check if the nested response indicates failure
      if (!response.data?.IsSuccess) {
        throw new Error(response.data?.Problem)
      }

      // Refresh the repair data after assignment
      await refreshRepairDetails()
      setSelectedExpertId("")
      toast.success("Η ανάθεση ολοκληρώθηκε επιτυχώς", { autoClose: 3000 })
    } catch (error) {
      console.error("Error assigning repair:", error)
      toast.error(error.message || error.message || "Αποτυχία ανάθεσης επισκευής")
    } finally {
      setIsSubmitting({ status: false, action: null })
      setShowAssignmentModal({ show: false, type: null })
    }
  }

  const isPreviewableFile = fileName => {
    return fileName.toLowerCase().match(/\.(jpg|jpeg|png|gif|pdf|webm)$/) !== null
  }

  const handleFileDownload = async file => {
    // Show modal immediately for previewable files
    if (isPreviewableFile(file.FileName)) {
      const previewableFiles = repair?.RepairFilesList.RepairFilesDetails.filter(f =>
        isPreviewableFile(f.FileName)
      )
      const index = previewableFiles.findIndex(f => f.FileID === file.FileID)
      setPreviewIndex(index)
      setPreviewFile(file)

      // Check if file is already cached
      if (fileCache[file.FileID]) {
        setPreviewUrl(fileCache[file.FileID])
        setShowPreviewModal(true)
        return
      }
      setPreviewUrl(null)
      setShowPreviewModal(true)
    } else {
      setDownloadingFileId(file.FileID)
    }
    setIsSubmitting({ status: true, action: "download" })

    try {
      const response = await get(`/repairs/${repairId}/files/${file.FileID}/${file.FileName}`, {
        responseType: "blob",
        transformResponse: [data => data]
      })

      // Backend may return JSON error payload instead of file content.
      if (response?.success === false) {
        toast.error(response?.error?.details || "Αποτυχία λήψης αρχείου")
        setShowPreviewModal(false)
        return
      }

      if (response?.data instanceof Blob) {
        const blobType = (response.data.type || "").toLowerCase()
        if (blobType.includes("application/json") || blobType.includes("text/json")) {
          try {
            const jsonText = await response.data.text()
            const json = JSON.parse(jsonText)
            if (json?.success === false) {
              toast.error(json?.error?.details || "Αποτυχία λήψης αρχείου")
              setShowPreviewModal(false)
              return
            }
          } catch (parseError) {
            // If JSON parsing fails, continue and treat as a normal file blob.
          }
        }
      }

      // Get content type from headers or determine from file extension
      const contentType =
        response.headers?.get("Content-Type") ||
        (file.FileName.toLowerCase().endsWith(".pdf")
          ? "application/pdf"
          : file.FileName.toLowerCase().endsWith(".webm")
          ? "video/webm"
          : file.FileName.toLowerCase().match(/\.(jpg|jpeg|png|gif)$/)
          ? "image/" + file.FileName.toLowerCase().split(".").pop()
          : "application/octet-stream")

      // Create a proper blob with the content type
      const blob = new Blob([response.data], { type: contentType })
      const url = window.URL.createObjectURL(blob)

      if (isPreviewableFile(file.FileName)) {
        // Cache the file URL
        setFileCache(prev => ({
          ...prev,
          [file.FileID]: url
        }))
        setPreviewUrl(url)
      } else {
        const link = document.createElement("a")
        link.href = url
        link.setAttribute("download", file.FileName)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      }
    } catch (error) {
      console.error("Error downloading file:", error)
      toast.error("Αποτυχία λήψης αρχείου")
      setShowPreviewModal(false)
    } finally {
      setIsSubmitting({ status: false, action: null })
      setDownloadingFileId(null)
    }
  }

  const handleClosePreview = () => {
    setShowPreviewModal(false)
    setPreviewFile(null)
    setPreviewUrl(null)
    setPreviewIndex(0)
  }

  const handleDownloadFromPreview = () => {
    if (previewFile && previewUrl) {
      const link = document.createElement("a")
      link.href = previewUrl
      link.setAttribute("download", previewFile.FileName)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }

  const handleDeleteFile = file => {
    setFileToDelete(file)
    setShowDeleteFileModal(true)
  }

  const handleConfirmDeleteFile = async () => {
    try {
      setIsSubmitting({ status: true, action: "delete" })
      const response = await deleteFile(`/repairs/${repairId}/files/${fileToDelete.FileID}`)
      if (response.success) {
        // Update the repair state directly by removing the deleted file
        setRepair(prev => ({
          ...prev,
          RepairFilesList: {
            ...prev.RepairFilesList,
            RepairFilesDetails: prev.RepairFilesList.RepairFilesDetails.filter(
              file => file.FileID !== fileToDelete.FileID
            )
          }
        }))

        // Clean up the file cache if it exists
        if (fileCache[fileToDelete.FileID]) {
          URL.revokeObjectURL(fileCache[fileToDelete.FileID])
          setFileCache(prev => {
            const newCache = { ...prev }
            delete newCache[fileToDelete.FileID]
            return newCache
          })
        }

        setShowDeleteFileModal(false)
        setFileToDelete(null)
        toast.success("Το αρχείο διαγράφηκε επιτυχώς", { autoClose: 3000 })
      }
    } catch (err) {
      console.error("Error deleting file:", err)
      toast.error("Αποτυχία διαγραφής αρχείου")
    } finally {
      setIsSubmitting({ status: false, action: null })
    }
  }

  const handleFileUpload = async (fileData, fromEexpert = false) => {
    const { fileName } = fileData

    try {
      setIsSubmitting({ status: true, action: "upload" })
      const response = await post(`/repairs/${repairId}/files`, {
        files: [fileData]
      })

      if (response.success && response.data?.IsSuccess) {
        if (!fromEexpert) {
          toast.success(fileName + " - Το αρχείο μεταφορτώθηκε επιτυχώς", { autoClose: 3000 })
          await refreshRepairDetails()
          setShowFileUpload(false)
        }
      } else {
        // Show the specific error message from the API
        const errorMessage = fileName + " - " + response.data?.Problem || "Αποτυχία ανεβάσματος αρχείου"
        toast.error(errorMessage)
      }
      return response
    } catch (err) {
      console.error("Error uploading file:", err)
      toast.error(fileName + " - Αποτυχία ανεβάσματος αρχείου")
      return { success: false, data: { IsSuccess: false } }
    } finally {
      setIsSubmitting({ status: false, action: null })
    }
  }

  const handleCompleteRepair = async () => {
    try {
      setIsSubmitting({ status: true, action: "completeRepair" })
      const payload = {
        repairId: repairId,
        GarageID: repair?.GarageID,
        actionType: 27,
        Status: 95
      }
      const response = await put(`/repairs/${repairId}`, payload)
      if (!response.data?.IsSuccess) {
        throw new Error(response.data?.Problem)
      }
      await refreshRepairDetails()
      toast.success("Η ολοκλήρωση επισκευής έγινε με επιτυχία")
    } catch (error) {
      console.error("Error completing repair:", error)
      toast.error(error.message || "Αποτυχία ολοκλήρωσης επισκευής")
    } finally {
      setIsSubmitting({ status: false, action: null })
    }
  }

  const handleComplete = async () => {
    try {
      setIsSubmitting({ status: true, action: "complete" })
      const payload = {
        repairId: repairId,
        actionType: 100,
        Status: 100
      }
      const response = await put(`/repairs/${repairId}`, payload)
      if (!response.data?.IsSuccess) {
        throw new Error(response.data?.Problem)
      }
      await refreshRepairDetails()
      toast.success("Η ολοκλήρωση διαδικασίας έγινε με επιτυχία")
    } catch (error) {
      console.error("Error completing repair:", error)
      toast.error(error.message || "Αποτυχία ολοκλήρωσης διαδικασίας")
    } finally {
      setIsSubmitting({ status: false, action: null })
    }
  }

  // Add the handleDownloadAll function near the other file handling functions
  const handleDownloadAll = async (filesArray = photoFiles, actionType = "downloadAll") => {
    try {
      setIsSubmitting({ status: true, action: actionType })
      const zip = new JSZip()
      const filesToDownload = filesArray
      const totalFiles = filesToDownload.length

      // Create a folder for each file type
      const folders = {}

      for (const [index, file] of filesToDownload.entries()) {
        // Update submission status with progress
        setIsSubmitting(prev => ({
          ...prev,
          progress: `Λήψη Αρχείων... (${index + 1}/${totalFiles})`
        }))

        try {
          // Get the file kind name for the folder
          const fileKindName = getAppLookupValue("FilesKind", file.FileKindID)

          // Create folder if it doesn't exist
          if (!folders[fileKindName]) {
            folders[fileKindName] = zip.folder(fileKindName)
          }

          // Get the file content
          const response = await get(`/repairs/${repairId}/files/${file.FileID}/${file.FileName}`, {
            responseType: "blob",
            transformResponse: [data => data]
          })

          // Add file to its corresponding folder
          folders[fileKindName].file(file.FileName, response.data)
        } catch (error) {
          console.error(`Error downloading file ${file.FileName}:`, error)
          toast.error(`Αποτυχία λήψης αρχείου: ${file.FileName}`)
        }
      }

      // Generate the zip file
      const content = await zip.generateAsync({ type: "blob" })

      // Create download link
      const url = window.URL.createObjectURL(content)
      const link = document.createElement("a")
      link.href = url
      link.download = `Αρχεία_Επισκευής_${repairId}.zip`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      toast.success("Η λήψη των αρχείων ολοκληρώθηκε", { autoClose: 3000 })
    } catch (error) {
      console.error("Error downloading all files:", error)
      toast.error("Αποτυχία λήψης αρχείων")
    } finally {
      setIsSubmitting({ status: false, action: null })
    }
  }

  const handleNavigatePreview = async direction => {
    const previewableFiles = repair?.RepairFilesList.RepairFilesDetails.filter(file =>
      isPreviewableFile(file.FileName)
    )

    let newIndex = previewIndex + direction
    if (newIndex < 0) newIndex = previewableFiles.length - 1
    if (newIndex >= previewableFiles.length) newIndex = 0

    setPreviewIndex(newIndex)
    const nextFile = previewableFiles[newIndex]
    setPreviewFile(nextFile)

    // Check if file is already cached
    if (fileCache[nextFile.FileID]) {
      setPreviewUrl(fileCache[nextFile.FileID])
      return
    }

    setPreviewUrl(null)

    try {
      const response = await get(`/repairs/${repairId}/files/${nextFile.FileID}/${nextFile.FileName}`, {
        responseType: "blob",
        transformResponse: [data => data]
      })

      const contentType =
        response.headers?.get("Content-Type") ||
        (nextFile.FileName.toLowerCase().endsWith(".pdf")
          ? "application/pdf"
          : nextFile.FileName.toLowerCase().endsWith(".webm")
          ? "video/webm"
          : nextFile.FileName.toLowerCase().match(/\.(jpg|jpeg|png|gif)$/)
          ? "image/" + nextFile.FileName.toLowerCase().split(".").pop()
          : "application/octet-stream")

      const blob = new Blob([response.data], { type: contentType })
      const url = window.URL.createObjectURL(blob)

      // Cache the file URL
      setFileCache(prev => ({
        ...prev,
        [nextFile.FileID]: url
      }))
      setPreviewUrl(url)
    } catch (error) {
      console.error("Error loading preview:", error)
      toast.error("Αποτυχία φόρτωσης αρχείου")
    }
  }

  const handleStartEExpert = useCallback(() => {
    setNotificationPhone(repair?.Owner?.Mobile ?? "")
    setNotificationEmail(repair?.Owner?.Mail ?? "")
    setNotificationPrefs({
      sms: false,
      email: false
    })
    setShowNotificationModal(true)
  }, [repair?.Owner?.Mobile, repair?.Owner?.Mail])

  const handleConfirmEExpert = async () => {
    try {
      setIsModalSubmitting(true)

      // Generate session ID using Web Crypto API
      const timestamp = Date.now().toString()
      const data = `${repairId.toString()}-${timestamp}`
      const encoder = new TextEncoder()
      const hashBuffer = await crypto.subtle.digest("SHA-256", encoder.encode(data))
      const hashArray = Array.from(new Uint8Array(hashBuffer))
      const sessionId = hashArray
        .map(b => b.toString(16).padStart(2, "0"))
        .join("")
        .substring(0, 16)

      // Check if any notification method is selected
      const hasNotificationMethod = notificationPrefs.sms || notificationPrefs.email

      // Only call the API if a notification method is selected
      if (hasNotificationMethod) {
        const payload = {
          sessionLink: `${process.env.REACT_APP_APP_URL}/user/${repairId}/${sessionId}`,
          repairId
        }

        // Add notification preferences to payload if selected
        if (notificationPrefs.sms) {
          payload.mobile = notificationPhone ?? repair.Owner.Mobile
        }
        if (notificationPrefs.email) {
          payload.email = notificationEmail ?? repair.Owner.Mail
        }

        const invitationResponse = await post("/send-session-invitation", payload)
        if (!invitationResponse.success) {
          if ("IsSuccess" in (invitationResponse.data || {}) && !invitationResponse.data.IsSuccess) {
            toast.error(invitationResponse.data?.Problem)
          }
          return
        }
        toast.success("Πρόσκληση συνεδρίας στάλθηκε επιτυχώς", { autoClose: 3000 })
      }

      const notificationText = hasNotificationMethod
        ? `Έναρξη διαδικασίας ExpertCall. Ειδοποίηση μέσω ${[
            notificationPrefs.sms && "SMS",
            notificationPrefs.email && "Email"
          ]
            .filter(Boolean)
            .join(" και ")}.`
        : "Έναρξη διαδικασίας ExpertCall."

      const historyResponse = await post(`/repairs/${repairId}/history`, {
        Descr: notificationText
      })

      if (!historyResponse.success || !historyResponse.data?.IsSuccess) {
        throw new Error(historyResponse.data?.Problem)
      }

      setShowNotificationModal(false)
      setShowEExpert(true)
      setSessionId(sessionId)
    } catch (error) {
      console.error("Failed to start e-expert session:", error)
      toast.error(error.message || "Αποτυχία έναρξης συνεδρίας ExpertCall.")
    } finally {
      setIsModalSubmitting(false)
    }
  }

  const handleOpenUploadModal = useCallback(fileKindIds => {
    setUploadFileKindIds(fileKindIds)
    setShowFileUpload(true)
  }, [])

  const handleCreateRepair = async () => {
    try {
      setIsSubmitting({ status: true, action: "create" })
      const response = await post("/repairs", repair)
      if (response.success && response.data?.IsSuccess) {
        toast.success("Η επισκευή δημιουργήθηκε επιτυχώς")
        setIsNew(false)
        setRepairId(response.data.ID)
        navigate(`/repairs/${response.data.ID}`, { state: { justCreated: true } })
      } else {
        throw new Error(response.data?.Problem)
      }
    } catch (err) {
      toast.error(err.message || "Αποτυχία δημιουργίας επισκευής")
    } finally {
      setIsSubmitting({ status: false, action: null })
    }
  }

  const handleOpenOfferActionModal = actionType => {
    setOfferActionComments("")
    setShowOfferActionModal({ show: true, actionType: actionType })
  }

  const handleConfirmOfferAction = async () => {
    try {
      setIsSubmitting({ status: true, action: showOfferActionModal.actionType })

      const payload = {
        repairId: repairId,
        Comments: offerActionComments
      }

      let successMessage = ""

      if (showOfferActionModal.actionType === "acceptOffer") {
        payload.actionType = 24
        successMessage = "Η προσφορά έγινε αποδεκτή με επιτυχία"
      } else if (showOfferActionModal.actionType === "reevaluateOffer") {
        payload.actionType = 20
        payload.Status = 50
        successMessage = "Το αίτημα για επανεξέταση της προσφοράς στάλθηκε με επιτυχία"
      } else if (showOfferActionModal.actionType === "rejectOffer") {
        payload.actionType = 24
        payload.SubStatusRejected = lookupOptions.subStatus.filter(status => 70 === status.Code)[0].SubCode
        successMessage = "Η προσφορά απορρίφθηκε με επιτυχία"
      }

      setIsSubmitting({ status: true, action: showOfferActionModal.actionType })

      const response = await put(`/repairs/${repairId}`, payload)

      setIsSubmitting({ status: false, action: null })

      if (!response.data?.IsSuccess) {
        throw new Error(response.data?.Problem)
      }

      await refreshRepairDetails()
      toast.success(successMessage)
    } catch (error) {
      console.error(`Error with offer action:`, error)
      toast.error(error.message || "Αποτυχία ενέργειας")
    } finally {
      setIsSubmitting({ status: false, action: null })
      setShowOfferActionModal({ show: false, actionType: null })
    }
  }

  const handleInformGarage = async () => {
    try {
      setIsSubmitting({ status: true, action: "informGarage" })
      const payload = {
        repairId: repairId,
        GarageID: repair?.GarageID,
        actionType: 26,
        Status: 90
      }
      const response = await put(`/repairs/${repairId}`, payload)
      if (!response.data?.IsSuccess) {
        throw new Error(response.data?.Problem)
      }
      await refreshRepairDetails()
      toast.success("Η ενημέρωση συνεργείου έγινε με επιτυχία")
    } catch (error) {
      console.error("Error informing garage:", error)
      toast.error(error.message || "Αποτυχία ενημέρωσης συνεργείου")
    } finally {
      setIsSubmitting({ status: false, action: null })
    }
  }

  const handleSendMessage = async () => {
    try {
      setIsSubmitting({ status: true, action: "sendMessage" })
      const response = await post("/messages", {
        ID: repairId,
        UserID: user.id,
        Descr: newMessage
      })

      if (response.success) {
        toast.success("Το μήνυμα στάλθηκε επιτυχώς.")
        setNewMessage("")
        await refreshRepairDetails()
      } else {
        throw new Error(response.data?.Problem)
      }
    } catch (error) {
      console.error("Error sending message:", error)
      toast.error(error.message || "Αποτυχία αποστολής μηνύματος.")
    } finally {
      setIsSubmitting({ status: false, action: null })
    }
  }

  return (
    <Layout
      showSidebar={true}
      showEExpert={showEExpert}
      sessionId={sessionId}
      onCloseEExpert={() => setShowEExpert(false)}
      disableEExpertIframe={isSubmitting.status && isSubmitting.action === "eexpert-cancel"}
      repairHistory={repair?.RepairHistoryList?.RepairHistoryDetails || []}
      loading={loading}
      repairId={repairId}
      appLookups={appLookups}
      onFileUpload={handleFileUpload}
      onComplete={handleCompleteRepair}
      refreshRepairDetails={refreshRepairDetails}
      setLoading={setLoading}>
      {loading ? (
        <div className="content-glass">
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <div className="mt-2">Φόρτωση λεπτομερειών επισκευής...</div>
          </div>
        </div>
      ) : pageError ? (
        <div className="p-3">
          <div className="content-glass">
            <div className="alert alert-danger d-flex align-items-center m-0">
              <i className="fas fa-exclamation-circle me-2" />
              {pageError}
            </div>
          </div>
        </div>
      ) : !repair ? (
        <div className="p-3">
          <div className="content-glass">
            <div className="alert alert-warning d-flex align-items-center m-0">
              <i className="fas fa-exclamation-triangle me-2" />Η επισκευή δε βρέθηκε.
            </div>
          </div>
        </div>
      ) : (
        <div className="content-glass mb-4 position-relative">
          {repair ? (
            <>
              {!showEExpert && <BackButton />}
              <div className="d-flex justify-content-between align-items-start mb-2 flex-wrap gap-4">
                <div className="d-flex justify-content-between gap-4 flex-wrap">
                  <h5 className="mb-0">{isNew ? "Νέα Επισκευή" : `Επισκευή #${repair?.RepairID || ""}`}</h5>
                </div>
                {!isNew && (
                  <div className="text-muted text-end">
                    <small>
                      <i className="fas fa-calendar me-1" />
                      Ημ. Καταχώρησης: {formatDateForDisplay(repair?.IssueDateTime)}
                    </small>
                    {repair?.CompletionDateTime && (
                      <>
                        <br />
                        <small>
                          <i className="fas fa-check-circle me-1" />
                          Ημ. Ολοκλήρωσης: {formatDateForDisplay(repair?.CompletionDateTime)}
                        </small>
                      </>
                    )}
                  </div>
                )}
                {!isNew && (
                  <>
                    <hr className="w-100 m-0" />
                    <div className="status-metro w-100">
                      <div className="status-metro-stations d-flex align-items-center justify-content-between w-100">
                        {(appLookups?.filter(l => l.Table === "Status") || [])
                          .sort((a, b) => a.Code - b.Code)
                          .map((st, idx, arr) => {
                            const isActive = st.Code === repair?.Status
                            const isPast = (repair?.Status ?? -Infinity) > st.Code
                            const isLast = idx === arr.length - 1
                            return (
                              <div
                                key={st.Code}
                                className="status-metro-item d-flex flex-column align-items-center">
                                <div
                                  className={`status-metro-dot ${
                                    (isActive || isPast) && `bg-status-${st.Code}`
                                  }`}
                                  style={{ opacity: isActive || isPast ? 1 : 0.35 }}
                                />
                                <div
                                  className="status-metro-label text-center"
                                  style={{
                                    opacity: isActive ? 1 : 0.5,
                                    fontWeight: isActive ? "bold" : "initial"
                                  }}>
                                  {st.Name}
                                </div>
                                {!isLast && (
                                  <div
                                    className={`status-metro-connector ${
                                      isPast ? `bg-status-${st.Code}` : ""
                                    }`}
                                  />
                                )}
                              </div>
                            )
                          })}
                      </div>
                    </div>
                    <hr className="w-100 m-0" />
                    {[60, 99].includes(repair?.Status) && (
                      <div className="ms-2 mt-1" style={{ fontSize: "0.85rem", opacity: 0.9 }}>
                        {getAppLookupValue("SubStatus", repair?.Status, repair?.SubStatusRejected) ||
                          "Άγνωστο"}
                      </div>
                    )}
                  </>
                )}
              </div>

              {[70, 80, 90, 95, 100].includes(repair?.Status) && (
                <>
                  <div className="d-flex justify-content-center gap-2 mb-2">
                    {!isGarage() && (
                      <div className="decisions">
                        <div className="decision">
                          <strong>
                            Απόφαση Πραγματογνώμονα:
                            {repair.SubStatusRejected === 10 ? (
                              <i className="fas fa-times text-danger" title="Απόρριψη" />
                            ) : (
                              <i className="fas fa-check text-success" title="Αποδοχή" />
                            )}
                          </strong>
                        </div>
                        <div className="decision">
                          <strong>
                            Απόφαση Διακανονιστή:
                            {repair.Status >= 80 ? (
                              <i className="fas fa-check text-success" title="Αποδοχή" />
                            ) : (
                              <i className="fas fa-question-circle text-muted" title="Αναμονή απόφασης" />
                            )}
                          </strong>
                        </div>
                      </div>
                    )}
                    {canOpenRepair && (
                      <Button
                        style={{ borderRadius: 0 }}
                        variant="outline-success"
                        onClick={handleReOpen}
                        disabled={isSubmitting.status}>
                        {isSubmitting.status && isSubmitting.action === "reopen" ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Άνοιγμα Επισκευής...
                          </>
                        ) : (
                          "Άνοιγμα Επισκευής"
                        )}
                      </Button>
                    )}
                  </div>
                  <hr className="mt-1 mb-2" />
                </>
              )}

              {/* Action Buttons */}
              <div className="mb-2" data-container-query>
                <ButtonGroup className="d-flex flex-column flex-container-md-row gap-2">
                  {!isNew && !showEExpert && (canEditVehicle || canEditInsured) && (
                    <Button
                      variant={isNew ? "success" : "primary"}
                      onClick={handleSave}
                      disabled={isSubmitting.status}>
                      {isSubmitting.status && isSubmitting.action === "save" ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Διόρθωση...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save me-2" />
                          Διόρθωση
                        </>
                      )}
                    </Button>
                  )}
                  {[10, 50].includes(repair?.Status) &&
                    !showEExpert &&
                    isGarage() &&
                    !(repair?.Status === 10 && isAdmin()) && (
                      <OverlayTrigger
                        placement="top"
                        overlay={getMissingDataTooltip()}
                        trigger={["hover", "focus"]}>
                        <span className="d-inline-block" style={{ flex: "1 1 auto" }}>
                          <Button
                            variant="success"
                            onClick={handleSubmitRepair}
                            disabled={
                              isSubmitting.status ||
                              registrationDocuments.length === 0 ||
                              financialOffers.length === 0 ||
                              photoFiles.length === 0 ||
                              !repair?.Vehicle?.Make ||
                              !repair?.Vehicle?.Model ||
                              !repair?.Insured?.Surname ||
                              !repair?.Insured?.Name ||
                              !repair?.Insured?.Mobile ||
                              !repair?.Insured?.Phone ||
                              !repair?.Insured?.TaxNumber
                            }
                            style={{
                              width: "100%",
                              borderRadius: 0
                            }}>
                            {isSubmitting.status && isSubmitting.action === "submission" ? (
                              <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Υποβολή...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-save me-2" />
                                {repair?.Status === 50 ? "Υποβολή Επανεξέτασης" : "Υποβολή Επισκευής"}
                              </>
                            )}
                          </Button>
                        </span>
                      </OverlayTrigger>
                    )}
                  {repair?.Status === 20 && isAdmin() && !showEExpert && (
                    <Button
                      variant="outline-secondary"
                      onClick={handleAssignRepair}
                      disabled={isSubmitting.status}>
                      <>
                        <i className="fas fa-user-plus me-2" />
                        Ανάθεση Επισκευής
                      </>
                    </Button>
                  )}
                  {repair?.Status === 1 && isAdmin() && !showEExpert && (
                    <Button
                      variant="outline-secondary"
                      onClick={handleChangeAssignment}
                      disabled={isSubmitting.status}>
                      {isSubmitting.status && isSubmitting.action === "changeAssignment" ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Αλλαγή...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-user-plus me-2" />
                          Αλλαγή Πραγματογνώμονα
                        </>
                      )}
                    </Button>
                  )}
                  {[40].includes(repair?.Status) &&
                    !(repair?.Status === 40 && isGarage()) &&
                    !showEExpert && (
                      <Button
                        variant="outline-warning"
                        onClick={handleCancelAssignment}
                        disabled={isSubmitting.status}>
                        {isSubmitting.status && isSubmitting.action === "cancelAssignment" ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Ακύρωση...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-times me-2" />
                            Ακύρωση Ανάθεσης
                          </>
                        )}
                      </Button>
                    )}
                  {!isNew &&
                    !showEExpert &&
                    ![60, 70, 80, 90, 95, 100].includes(repair?.Status) &&
                    (20 !== repair?.Status || !isGarage()) &&
                    (50 !== repair?.Status || !isExpert()) &&
                    !(repair?.Status === 40 && isGarage()) && (
                      <Button
                        variant="outline-danger"
                        onClick={handleCancelRepair}
                        disabled={isSubmitting.status}>
                        {isSubmitting.status && isSubmitting.action === "cancelRepair" ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Ακύρωση...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-ban me-2" />
                            Ακύρωση Επισκευής
                          </>
                        )}
                      </Button>
                    )}
                  {[90].includes(repair?.Status) && isGarage() && !showEExpert && (
                    <OverlayTrigger
                      placement="top"
                      overlay={getMissingDataTooltip(true)}
                      trigger={["hover", "focus"]}>
                      <span className="d-inline-block mx-auto">
                        <Button
                          variant="success"
                          onClick={handleCompleteRepair}
                          disabled={
                            isSubmitting.status ||
                            invoices.length === 0 ||
                            repairedVehiclePhotoFiles.length === 0 ||
                            loipaDocuments.length === 0
                          }>
                          {isSubmitting.status && isSubmitting.action === "completeRepair" ? (
                            <>
                              <Spinner animation="border" size="sm" className="me-2" />
                              Ολοκλήρωση...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-check me-2" />
                              Ολοκλήρωση Επισκευής
                            </>
                          )}
                        </Button>
                      </span>
                    </OverlayTrigger>
                  )}
                  {[95].includes(repair?.Status) && isAdmin() && !showEExpert && (
                    <OverlayTrigger
                      placement="top"
                      overlay={getMissingDataTooltip(true)}
                      trigger={["hover", "focus"]}>
                      <span className="d-inline-block">
                        <Button
                          variant="success"
                          onClick={handleComplete}
                          disabled={
                            isSubmitting.status ||
                            invoices.length === 0 ||
                            repairedVehiclePhotoFiles.length === 0 ||
                            loipaDocuments.length === 0
                          }>
                          {isSubmitting.status && isSubmitting.action === "complete" ? (
                            <>
                              <Spinner animation="border" size="sm" className="me-2" />
                              Ολοκλήρωση...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-check me-2" />
                              Ολοκλήρωση Διαδικασίας
                            </>
                          )}
                        </Button>
                      </span>
                    </OverlayTrigger>
                  )}
                  {[80].includes(repair?.Status) && isAdmin() && !showEExpert && (
                    <Button variant="success" onClick={handleInformGarage} disabled={isSubmitting.status}>
                      {isSubmitting.status && isSubmitting.action === "informGarage" ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Ενημέρωση Συνεργείου...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-check me-2" />
                          Ενημέρωση Συνεργείου
                        </>
                      )}
                    </Button>
                  )}
                  {repair?.Status === 60 && (
                    <Button variant="outline-success" onClick={handleRestore} disabled={isSubmitting.status}>
                      {isSubmitting.status && isSubmitting.action === "restore" ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Επαναφορά...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-undo me-2" />
                          Επαναφορά
                        </>
                      )}
                    </Button>
                  )}
                  {!showEExpert && canTriggerExpertCall && (
                    <Button
                      variant="outline-secondary"
                      onClick={handleStartEExpert}
                      disabled={isSubmitting.status}>
                      {isSubmitting.status &&
                      (isSubmitting.action === "eexpert-start" ||
                        isSubmitting.action === "eexpert-cancel") ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          "Έναρξη..."
                        </>
                      ) : (
                        <>
                          <i className={`fas fa-play me-2`} />
                          Έναρξη διαδικασίας ExpertCall
                        </>
                      )}
                    </Button>
                  )}
                  {canCancelExpertDecision && (
                    <Button
                      variant="outline-danger"
                      onClick={handleCancelExpertDecision}
                      disabled={isSubmitting.status}>
                      {isSubmitting.status && isSubmitting.action === "cancelExpertDecision" ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Ακύρωση...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-times me-2" />
                          Ακύρωση Απόφασης Πραγματογνώμονα
                        </>
                      )}
                    </Button>
                  )}
                </ButtonGroup>
              </div>

              {isExpert() && repair?.Status === 40 && !showEExpert && (
                <div className="mb-2" data-container-query>
                  <hr className="m-0" />
                  <ButtonGroup className="mt-2 d-flex flex-column flex-container-md-row gap-2">
                    <Button
                      variant="success"
                      onClick={() => handleOpenOfferActionModal("acceptOffer")}
                      disabled={isSubmitting.status}>
                      {isSubmitting.status && isSubmitting.action === "acceptOffer" ? (
                        <>
                          <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                          <span className="ms-2">Αποδοχή...</span>
                        </>
                      ) : (
                        "Αποδοχή προσφοράς"
                      )}
                    </Button>
                    <Button
                      variant="warning"
                      onClick={() => handleOpenOfferActionModal("reevaluateOffer")}
                      disabled={isSubmitting.status}>
                      {isSubmitting.status && isSubmitting.action === "reevaluateOffer" ? (
                        <>
                          <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                          <span className="ms-2">Επανεξέταση...</span>
                        </>
                      ) : (
                        "Επανεξέταση προσφοράς"
                      )}
                    </Button>
                    <Button
                      variant="danger"
                      onClick={() => handleOpenOfferActionModal("rejectOffer")}
                      disabled={isSubmitting.status}>
                      {isSubmitting.status && isSubmitting.action === "rejectOffer" ? (
                        <>
                          <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                          <span className="ms-2">Απόρριψη...</span>
                        </>
                      ) : (
                        "Απόρριψη προσφοράς"
                      )}
                    </Button>
                  </ButtonGroup>
                </div>
              )}

              {/* Offer Action Modal */}
              <Modal
                show={showOfferActionModal.show}
                onHide={() => setShowOfferActionModal({ show: false, actionType: null })}>
                <Modal.Header closeButton>
                  <Modal.Title>
                    {showOfferActionModal.actionType === "acceptOffer" && (
                      <i className="fas fa-check me-2 text-success" />
                    )}
                    {showOfferActionModal.actionType === "reevaluateOffer" && (
                      <i className="fas fa-undo me-2 text-warning" />
                    )}
                    {showOfferActionModal.actionType === "rejectOffer" && (
                      <i className="fas fa-times me-2 text-danger" />
                    )}
                    {showOfferActionModal.actionType === "acceptOffer"
                      ? "Αποδοχή Προσφοράς"
                      : showOfferActionModal.actionType === "reevaluateOffer"
                      ? "Επανεξέταση Προσφοράς"
                      : "Απόρριψη Προσφοράς"}
                  </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <Form>
                    <Form.Group>
                      <Form.Label>Σχόλια:</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={offerActionComments}
                        onChange={e => setOfferActionComments(e.target.value)}
                        placeholder="Εισάγετε τα σχόλιά σας εδώ..."
                      />
                    </Form.Group>
                  </Form>
                </Modal.Body>
                <Modal.Footer>
                  <Button
                    variant="secondary"
                    onClick={() => setShowOfferActionModal({ show: false, actionType: null })}
                    disabled={isSubmitting.status}>
                    Ακύρωση
                  </Button>
                  <Button
                    variant={
                      showOfferActionModal.actionType === "acceptOffer"
                        ? "success"
                        : showOfferActionModal.actionType === "reevaluateOffer"
                        ? "warning"
                        : "danger"
                    }
                    onClick={handleConfirmOfferAction}
                    disabled={isSubmitting.status}>
                    {isSubmitting.status ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Επεξεργασία...
                      </>
                    ) : (
                      <>
                        {showOfferActionModal.actionType === "acceptOffer" && (
                          <i className="fas fa-check me-2" />
                        )}
                        {showOfferActionModal.actionType === "reevaluateOffer" && (
                          <i className="fas fa-undo me-2" />
                        )}
                        {showOfferActionModal.actionType === "rejectOffer" && (
                          <i className="fas fa-times me-2" />
                        )}
                        {showOfferActionModal.actionType === "acceptOffer"
                          ? "Αποδοχή"
                          : showOfferActionModal.actionType === "reevaluateOffer"
                          ? "Επανεξέταση"
                          : "Απόρριψη"}
                      </>
                    )}
                  </Button>
                </Modal.Footer>
              </Modal>

              {/* Notification Preferences Modal */}
              <Modal
                show={showNotificationModal}
                onHide={() => setShowNotificationModal(false)}
                backdrop={true}
                keyboard={true}>
                <Modal.Header closeButton={true}>
                  <Modal.Title>
                    <i className="fas fa-bell me-2" />
                    Επιλογές Ειδοποίησης
                  </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <Form>
                    <Form.Group>
                      <Form.Label>Μέθοδος Ειδοποίησης</Form.Label>
                      <div className="notification-methods">
                        <div className="d-flex align-items-center mb-2 gap-2">
                          <Form.Check
                            type="checkbox"
                            label="SMS"
                            checked={notificationPrefs.sms}
                            onChange={e => setNotificationPrefs(prev => ({ ...prev, sms: e.target.checked }))}
                            className="mb-0"
                          />
                          <Form.Control
                            type="text"
                            value={notificationPhone}
                            onChange={e => setNotificationPhone(e.target.value)}
                            placeholder="Εισάγετε τηλέφωνο επικοινωνίας"
                            disabled={!notificationPrefs.sms}
                            className="flex-grow-1"
                          />
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <Form.Check
                            type="checkbox"
                            label="Email"
                            checked={notificationPrefs.email}
                            onChange={e =>
                              setNotificationPrefs(prev => ({ ...prev, email: e.target.checked }))
                            }
                            className="mb-0"
                          />
                          <Form.Control
                            type="email"
                            value={notificationEmail}
                            onChange={e => setNotificationEmail(e.target.value)}
                            placeholder="Εισάγετε email επικοινωνίας"
                            disabled={!notificationPrefs.email}
                            className="flex-grow-1"
                          />
                        </div>
                      </div>
                    </Form.Group>
                  </Form>
                </Modal.Body>
                <Modal.Footer>
                  <Button
                    variant="outline-secondary"
                    onClick={() => {
                      setShowNotificationModal(false)
                    }}>
                    Ακύρωση
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleConfirmEExpert}
                    disabled={
                      isModalSubmitting ||
                      (notificationPrefs.sms && !notificationPhone) ||
                      (notificationPrefs.email && !notificationEmail)
                    }>
                    {isModalSubmitting ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Έναρξη...
                      </>
                    ) : (
                      "Έναρξη"
                    )}
                  </Button>
                </Modal.Footer>
              </Modal>

              {/* Submit Confirmation Modal */}
              <Modal show={showSubmitConfirmationModal} onHide={() => setShowSubmitConfirmationModal(false)}>
                <Modal.Header closeButton>
                  <Modal.Title>
                    <i className="fas fa-question-circle me-2 text-warning" />
                    Επιβεβαίωση Υποβολής
                  </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <Form>
                    <Form.Group>
                      <Form.Label>Σχόλια (προαιρετικά):</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={submitComment}
                        onChange={e => setSubmitComment(e.target.value)}
                        placeholder="Εισάγετε τα σχόλιά σας εδώ..."
                      />
                    </Form.Group>
                  </Form>
                </Modal.Body>
                <Modal.Footer>
                  <Button
                    variant="secondary"
                    onClick={() => setShowSubmitConfirmationModal(false)}
                    disabled={isSubmitting.status}>
                    Ακύρωση
                  </Button>
                  <Button
                    variant="success"
                    onClick={handleConfirmSubmitRepair}
                    disabled={isSubmitting.status}>
                    {isSubmitting.status ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Υποβολή...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-check me-2" />
                        Υποβολή
                      </>
                    )}
                  </Button>
                </Modal.Footer>
              </Modal>

              {/* Rejection Comments Modal */}
              <Modal show={showRejectionModal} onHide={() => setShowRejectionModal(false)}>
                <Modal.Header closeButton>
                  <Modal.Title>
                    <i className="fas fa-ban me-2 text-danger" />
                    Ακύρωση Επισκευής
                  </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <Form>
                    <Form.Group className="mb-3">
                      <Form.Label>Αιτία Ακύρωσης:</Form.Label>
                      <Form.Select
                        value={selectedSubStatus}
                        onChange={e => setSelectedSubStatus(e.target.value)}
                        isInvalid={!selectedSubStatus}>
                        <option value="">Επιλέξτε αιτία</option>
                        {lookupOptions.subStatus
                          .filter(status => 60 === status.Code)
                          .map(status => (
                            <option key={status.SubCode} value={status.SubCode}>
                              {status.Name}
                            </option>
                          ))}
                      </Form.Select>
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Σχόλια:</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={rejectionComments}
                        onChange={e => setRejectionComments(e.target.value)}
                        placeholder="Εισάγετε τα σχόλιά σας εδώ..."
                        isInvalid={!rejectionComments}
                      />
                    </Form.Group>
                  </Form>
                </Modal.Body>
                <Modal.Footer>
                  <Button
                    variant="secondary"
                    onClick={() => setShowRejectionModal(false)}
                    disabled={isSubmitting.status}>
                    Ακύρωση
                  </Button>
                  <Button
                    variant="danger"
                    onClick={handleConfirmCancelRepair}
                    disabled={!selectedSubStatus || !rejectionComments || isSubmitting.status}>
                    {isSubmitting.status ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Ακύρωση Επισκευής...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-ban me-2" />
                        Ακύρωση Επισκευής
                      </>
                    )}
                  </Button>
                </Modal.Footer>
              </Modal>

              {/* Cancel Assignment Modal */}
              <Modal show={showCancelAssignmentModal} onHide={() => setShowCancelAssignmentModal(false)}>
                <Modal.Header closeButton>
                  <Modal.Title>
                    <i className="fas fa-times me-2 text-warning" />
                    Ακύρωση Ανάθεσης
                  </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <Form>
                    <Form.Group className="mb-3">
                      <Form.Label>Αιτία Ακύρωσης:</Form.Label>
                      <Form.Select
                        value={selectedSubStatus}
                        onChange={e => setSelectedSubStatus(e.target.value)}
                        isInvalid={!selectedSubStatus}>
                        <option value="">Επιλέξτε αιτία</option>
                        {lookupOptions.subStatus
                          .filter(status => 10 === status.Code)
                          .map(status => (
                            <option key={status.SubCode} value={status.SubCode}>
                              {status.Name}
                            </option>
                          ))}
                      </Form.Select>
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Σχόλια:</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={cancelAssignmentComments}
                        onChange={e => setCancelAssignmentComments(e.target.value)}
                        placeholder="Εισάγετε τα σχόλιά σας εδώ..."
                        isInvalid={!cancelAssignmentComments}
                      />
                    </Form.Group>
                  </Form>
                </Modal.Body>
                <Modal.Footer>
                  <Button
                    variant="secondary"
                    onClick={() => setShowCancelAssignmentModal(false)}
                    disabled={isSubmitting.status}>
                    Ακύρωση
                  </Button>
                  <Button
                    variant="warning"
                    onClick={handleConfirmCancelAssignment}
                    disabled={!selectedSubStatus || !cancelAssignmentComments || isSubmitting.status}>
                    {isSubmitting.status ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Ακύρωση Ανάθεσης...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-times me-2" />
                        Ακύρωση Ανάθεσης
                      </>
                    )}
                  </Button>
                </Modal.Footer>
              </Modal>

              {/* Assignment Modal */}
              <Modal
                show={showAssignmentModal.show}
                onHide={() => setShowAssignmentModal({ show: false, type: null })}>
                <Modal.Header closeButton>
                  <Modal.Title>
                    <i
                      className={`fas ${
                        showAssignmentModal.type === "change" ? "fa-exchange-alt" : "fa-user-plus"
                      } me-2 ${showAssignmentModal.type === "change" ? "text-info" : "text-success"}`}
                    />
                    {showAssignmentModal.type === "change" ? "Αλλαγή Ανάθεσης" : "Ανάθεση Επισκευής"}
                  </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <Form>
                    <Form.Group className="mb-3">
                      <Form.Label>Πραγματογνώμονας:</Form.Label>
                      <Form.Select
                        value={selectedExpertId}
                        onChange={e => setSelectedExpertId(e.target.value)}
                        isInvalid={!selectedExpertId}
                        disabled={isSubmitting.status}>
                        <option value="">Επιλέξτε πραγματογνώμονα</option>
                        {users.map(user => (
                          <option key={user.UserID} value={user.UserID}>
                            {user.Firstname} {user.LastName}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                    {/*<Form.Group className="mb-3 d-flex align-items-center">
                      <Form.Label className="me-3">Φυσική Παρουσία</Form.Label>
                      <Form.Check
                        type="radio"
                        label="Ναι"
                        name="physicalPresence"
                        id="physicalPresenceYes"
                        className="me-3"
                      />
                      <Form.Check
                        type="radio"
                        label="Όχι"
                        name="physicalPresence"
                        id="physicalPresenceNo"
                        defaultChecked
                      />
                    </Form.Group>*/}
                  </Form>
                </Modal.Body>
                <Modal.Footer>
                  <Button
                    variant="outline-secondary"
                    onClick={() => setShowAssignmentModal({ show: false, type: null })}
                    disabled={isSubmitting.status}>
                    Ακύρωση
                  </Button>
                  <Button
                    variant={showAssignmentModal.type === "change" ? "outline-info" : "outline-success"}
                    onClick={handleConfirmAssignment}
                    disabled={
                      !selectedExpertId ||
                      isSubmitting.status ||
                      (showAssignmentModal.type === "change" &&
                        selectedExpertId === repair?.ExpertID?.toString())
                    }>
                    {isSubmitting.status ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        {showAssignmentModal.type === "change" ? "Αλλαγή..." : "Ανάθεση..."}
                      </>
                    ) : (
                      <>
                        <i
                          className={`fas ${
                            showAssignmentModal.type === "change" ? "fa-exchange-alt" : "fa-user-plus"
                          } me-2`}
                        />
                        {showAssignmentModal.type === "change" ? "Αλλαγή Ανάθεσης" : "Ανάθεση"}
                      </>
                    )}
                  </Button>
                </Modal.Footer>
              </Modal>

              {/* Delete File Confirmation Modal */}
              <Modal show={showDeleteFileModal} onHide={() => setShowDeleteFileModal(false)}>
                <Modal.Header closeButton>
                  <Modal.Title>
                    <i className="fas fa-trash me-2 text-danger" />
                    Διαγραφή Αρχείου
                  </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  Είστε βέβαιοι ότι θέλετε να διαγράψετε το αρχείο{" "}
                  <span style={{ wordBreak: "break-all" }}>"{fileToDelete?.FileName}"</span>;
                </Modal.Body>
                <Modal.Footer>
                  <Button
                    variant="secondary"
                    onClick={() => setShowDeleteFileModal(false)}
                    disabled={isSubmitting.status}>
                    Ακύρωση
                  </Button>
                  <Button variant="danger" onClick={handleConfirmDeleteFile} disabled={isSubmitting.status}>
                    {isSubmitting.status ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Διαγραφή...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-trash me-2" />
                        Διαγραφή
                      </>
                    )}
                  </Button>
                </Modal.Footer>
              </Modal>

              {/* Preview Modal */}
              <Modal show={showPreviewModal} onHide={handleClosePreview} size="lg">
                <Modal.Header closeButton>
                  <Modal.Title>
                    <i
                      className={`fas ${
                        previewFile?.FileName.toLowerCase().match(/\.(jpg|jpeg|png|gif)$/)
                          ? "fa-image"
                          : previewFile?.FileName.toLowerCase().endsWith(".pdf")
                          ? "fa-file-pdf"
                          : previewFile?.FileName.toLowerCase().endsWith(".webm")
                          ? "fa-file-video"
                          : "fa-file"
                      } me-2`}
                    />
                    {previewFile?.FileName}
                  </Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center position-relative">
                  {!previewUrl ? (
                    <div className="py-5">
                      <Spinner animation="border" variant="primary" />
                      <div className="text-muted mt-3">Φόρτωση αρχείου...</div>
                    </div>
                  ) : (
                    <>
                      {/* Navigation Arrows */}
                      <div
                        className="position-absolute top-50 start-0 translate-middle-y"
                        style={{ zIndex: 1000, left: "20px" }}>
                        <Button
                          variant="light"
                          className="rounded-circle shadow-sm"
                          onClick={() => handleNavigatePreview(-1)}
                          style={{
                            width: "40px",
                            height: "40px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            margin: "0 20px"
                          }}>
                          <i className="fas fa-chevron-left" />
                        </Button>
                      </div>
                      <div
                        className="position-absolute top-50 end-0 translate-middle-y"
                        style={{ zIndex: 1000, right: "20px" }}>
                        <Button
                          variant="light"
                          className="rounded-circle shadow-sm"
                          onClick={() => handleNavigatePreview(1)}
                          style={{
                            width: "40px",
                            height: "40px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            margin: "0 20px"
                          }}>
                          <i className="fas fa-chevron-right" />
                        </Button>
                      </div>

                      {/* Preview Content */}
                      {previewFile?.FileName?.toLowerCase().endsWith(".pdf") ? (
                        <iframe src={previewUrl} width="100%" height="600px" title="PDF Preview" />
                      ) : previewFile?.FileName.toLowerCase().endsWith(".webm") ? (
                        <div
                          className="position-relative"
                          style={{
                            maxWidth: "100%",
                            maxHeight: "600px",
                            overflow: "hidden"
                          }}
                          ref={videoContainerRef}
                          onMouseMove={e => {
                            if (zoomMode === "none" || !videoRef.current) return
                            const rect = videoRef.current.getBoundingClientRect()
                            const x = ((e.clientX - rect.left) / rect.width) * 100
                            const y = ((e.clientY - rect.top) / rect.height) * 100
                            setZoomPosition({ x, y })

                            if (zoomMode === "scale") {
                              videoRef.current.style.transformOrigin = `${x}% ${y}%`
                            }
                          }}
                          onMouseLeave={() => {
                            if (zoomMode === "lens") {
                              setZoomPosition({ x: 50, y: 50 })
                            }
                          }}>
                          <video
                            ref={videoRef}
                            controls
                            autoPlay={false}
                            style={{
                              maxWidth: "100%",
                              maxHeight: "70vh",
                              transform: zoomMode === "scale" ? `scale(${zoomLevel})` : "none",
                              cursor: zoomMode !== "none" ? "move" : "default",
                              transition: "transform 0.1s ease-out"
                            }}
                            src={previewUrl || `data:video/webm;base64,${previewFile?.FileByte}`}
                            className="shadow">
                            <source type="video/webm" />
                            Your browser does not support the video tag.
                          </video>
                          {zoomMode === "scale" && (
                            <div className="zoom-controls position-absolute top-0 end-0 m-3 bg-white rounded shadow p-2">
                              <Button
                                variant="light"
                                size="sm"
                                onClick={() => setZoomLevel(prev => Math.min(prev + 0.5, 3))}
                                className="me-2">
                                <i className="fas fa-plus" />
                              </Button>
                              <Button
                                variant="light"
                                size="sm"
                                onClick={() => setZoomLevel(prev => Math.max(prev - 0.5, 1))}>
                                <i className="fas fa-minus" />
                              </Button>
                            </div>
                          )}
                          {zoomMode === "lens" && (
                            <div
                              className="zoom-lens"
                              style={{
                                position: "absolute",
                                width: "150px",
                                height: "150px",
                                border: "2px solid #fff",
                                borderRadius: "50%",
                                pointerEvents: "none",
                                overflow: "hidden",
                                boxShadow: "0 0 10px rgba(0,0,0,0.5)",
                                left: `${zoomPosition.x}%`,
                                top: `${zoomPosition.y}%`,
                                transform: "translate(-50%, -50%)"
                              }}>
                              <video
                                src={previewUrl}
                                style={{
                                  position: "absolute",
                                  left: `${-zoomPosition.x}%`,
                                  top: `${-zoomPosition.y}%`,
                                  width: "300%",
                                  height: "300%",
                                  objectFit: "cover",
                                  objectPosition: `${zoomPosition.x}% ${zoomPosition.y}%`
                                }}
                                autoPlay={false}
                                muted
                              />
                            </div>
                          )}
                        </div>
                      ) : (
                        <div
                          className="position-relative"
                          style={{
                            maxWidth: "100%",
                            maxHeight: "600px",
                            overflow: "hidden"
                          }}
                          onMouseMove={e => {
                            if (zoomMode === "none" || !imageRef.current) return
                            const rect = imageRef.current.getBoundingClientRect()
                            const x = ((e.clientX - rect.left) / rect.width) * 100
                            const y = ((e.clientY - rect.top) / rect.height) * 100
                            setZoomPosition({ x, y })

                            if (zoomMode === "scale") {
                              imageRef.current.style.transformOrigin = `${x}% ${y}%`
                            }
                          }}
                          onMouseLeave={() => {
                            if (zoomMode === "lens") {
                              setZoomPosition({ x: 50, y: 50 })
                            }
                          }}>
                          <img
                            ref={imageRef}
                            src={previewUrl}
                            alt="Preview"
                            className="img-fluid"
                            style={{
                              maxHeight: "600px",
                              transform: zoomMode === "scale" ? `scale(${zoomLevel})` : "none",
                              cursor: zoomMode !== "none" ? "move" : "default",
                              transition: "transform 0.1s ease-out"
                            }}
                          />
                          {zoomMode === "scale" && (
                            <div className="zoom-controls position-absolute top-0 end-0 m-3 bg-white rounded shadow p-2">
                              <Button
                                variant="light"
                                size="sm"
                                onClick={() => setZoomLevel(prev => Math.min(prev + 0.5, 3))}
                                className="me-2">
                                <i className="fas fa-plus" />
                              </Button>
                              <Button
                                variant="light"
                                size="sm"
                                onClick={() => setZoomLevel(prev => Math.max(prev - 0.5, 1))}>
                                <i className="fas fa-minus" />
                              </Button>
                            </div>
                          )}
                          {zoomMode === "lens" && (
                            <div
                              className="zoom-lens"
                              style={{
                                position: "absolute",
                                width: "150px",
                                height: "150px",
                                border: "2px solid #fff",
                                borderRadius: "50%",
                                pointerEvents: "none",
                                overflow: "hidden",
                                boxShadow: "0 0 10px rgba(0,0,0,0.5)",
                                left: `${zoomPosition.x}%`,
                                top: `${zoomPosition.y}%`,
                                transform: "translate(-50%, -50%)"
                              }}>
                              <img
                                src={previewUrl}
                                alt="Zoomed"
                                style={{
                                  position: "absolute",
                                  left: `${-zoomPosition.x}%`,
                                  top: `${-zoomPosition.y}%`,
                                  width: "300%",
                                  height: "300%",
                                  objectFit: "cover",
                                  objectPosition: `${zoomPosition.x}% ${zoomPosition.y}%`
                                }}
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </Modal.Body>
                <Modal.Footer>
                  <div className="d-flex gap-2 w-100">
                    <div className="me-auto">
                      {(previewFile?.FileName.toLowerCase().match(/\.(jpg|jpeg|png|gif)$/) ||
                        previewFile?.FileName.toLowerCase().endsWith(".webm")) && (
                        <div className="d-flex gap-2">
                          <Button
                            variant={zoomMode !== "none" ? "primary" : "outline-primary"}
                            onClick={() => {
                              setZoomMode(prev => (prev === "none" ? "scale" : "none"))
                              if (zoomMode === "none") {
                                setZoomLevel(1)
                              }
                            }}>
                            <i
                              className={`fas ${zoomMode === "none" ? "fa-search" : "fa-search-minus"} me-2`}
                            />
                            {zoomMode === "none" ? "Ενεργοποίηση Μεγέθυνσης" : "Απενεργοποίηση Μεγέθυνσης"}
                          </Button>
                          {zoomMode !== "none" && (
                            <Button
                              variant="outline-primary"
                              onClick={() => {
                                setZoomMode(prev => (prev === "scale" ? "lens" : "scale"))
                                setZoomLevel(1)
                              }}>
                              <i
                                className={`fas ${
                                  zoomMode === "scale" ? "fa-search-plus" : "fa-search"
                                } me-2`}
                              />
                              {zoomMode === "scale" ? "Κλίμακα" : "Φακός"}
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                    <Button variant="secondary" onClick={handleClosePreview}>
                      <i className="fas fa-times me-2" />
                      Κλείσιμο
                    </Button>
                    <Button variant="primary" onClick={handleDownloadFromPreview} disabled={!previewUrl}>
                      <i className="fas fa-download me-2" />
                      Λήψη
                    </Button>
                  </div>
                </Modal.Footer>
              </Modal>

              <hr className="mt-0 mb-0" />
              <div className="row g-4 mt-2">
                <div className="col-12 col-lg-6">
                  <Card className="h-100">
                    <Card.Header className="bg-navy text-white">
                      <h6 className="mb-0">Στοιχεία οχήματος</h6>
                    </Card.Header>
                    <Card.Body>
                      <Row className="g-3">
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>Μάρκα</Form.Label>
                            <Form.Control
                              type="text"
                              isInvalid={repair?.Vehicle?.Make === ""}
                              value={repair?.Vehicle?.Make || ""}
                              onChange={e => handleInputChange("Vehicle.Make", e.target.value.toUpperCase())}
                              disabled={isSubmitting.status || !canEditVehicle}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>Μοντέλο</Form.Label>
                            <Form.Control
                              type="text"
                              isInvalid={repair?.Vehicle?.Model === ""}
                              value={repair?.Vehicle?.Model || ""}
                              onChange={e => handleInputChange("Vehicle.Model", e.target.value.toUpperCase())}
                              disabled={isSubmitting.status || !canEditVehicle}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>Αρ. Κυκλοφορίας</Form.Label>
                            <Form.Control
                              disabled={true}
                              type="text"
                              value={repair?.Vehicle?.PlateNumber || ""}
                              onChange={e => handleInputChange("Vehicle.PlateNumber", e.target.value)}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>Παρατηρήσεις</Form.Label>
                            <textarea
                              className="form-control"
                              rows={4}
                              value={repair?.Vehicle?.Notes || ""}
                              onChange={e => handleInputChange("Vehicle.Notes", e.target.value)}
                              disabled={isSubmitting.status || !canEditVehicle}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </div>

                <div className="col-12 col-lg-6">
                  <Card className="h-100">
                    <Card.Header className="bg-navy text-white">
                      <h6 className="mb-0">Στοιχεία Ζημιωθέντα</h6>
                    </Card.Header>
                    <Card.Body>
                      <Row className="g-3">
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>Επώνυμο</Form.Label>
                            <Form.Control
                              type="text"
                              isInvalid={repair?.Insured?.Surname === ""}
                              value={repair?.Insured?.Surname || ""}
                              onChange={e =>
                                handleInputChange("Insured.Surname", e.target.value.toUpperCase())
                              }
                              disabled={isSubmitting.status || !canEditInsured}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>Όνομα</Form.Label>
                            <Form.Control
                              type="text"
                              isInvalid={repair?.Insured?.Name === ""}
                              value={repair?.Insured?.Name || ""}
                              onChange={e => handleInputChange("Insured.Name", e.target.value.toUpperCase())}
                              disabled={isSubmitting.status || !canEditInsured}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>Κινητό</Form.Label>
                            <Form.Control
                              type="text"
                              isInvalid={repair?.Insured?.Mobile === ""}
                              value={repair?.Insured?.Mobile || ""}
                              onChange={e => handleInputChange("Insured.Mobile", e.target.value)}
                              disabled={isSubmitting.status || !canEditInsured}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>Τηλ. Επικοινωνίας</Form.Label>
                            <Form.Control
                              type="text"
                              isInvalid={repair?.Insured?.Phone === ""}
                              value={repair?.Insured?.Phone || ""}
                              onChange={e => handleInputChange("Insured.Phone", e.target.value)}
                              disabled={isSubmitting.status || !canEditInsured}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>Α.Φ.Μ.</Form.Label>
                            <Form.Control
                              type="text"
                              isInvalid={repair?.Insured?.TaxNumber === ""}
                              value={repair?.Insured?.TaxNumber || ""}
                              onChange={e => handleInputChange("Insured.TaxNumber", e.target.value)}
                              disabled={isSubmitting.status || !canEditInsured}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </div>

                <div className="col-12">
                  <Card>
                    <Card.Header className="bg-navy text-white">
                      <h6 className="mb-0">Στοιχεία Επισκευής</h6>
                    </Card.Header>
                    <Card.Body>
                      <Row className="g-3">
                        <Col md={3}>
                          <Form.Group>
                            <Form.Label>Ασφαλιστική Εταιρεία</Form.Label>
                            <Form.Select
                              value={repair?.InsuranceCompany || ""}
                              onChange={e => handleInputChange("InsuranceCompany", e.target.value)}
                              disabled={true}>
                              <option value="">Επιλέξτε Ασφαλιστική</option>
                              {lookupOptions.companies
                                .sort((a, b) => a.Name.localeCompare(b.Name))
                                .map(company => (
                                  <option key={company.Code} value={company.Code}>
                                    {company.Name}
                                  </option>
                                ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group>
                            <Form.Label>Αρ. Φακέλου</Form.Label>
                            <Form.Control
                              type="text"
                              value={repair?.ClaimNo || ""}
                              onChange={e => handleInputChange("ClaimNo", e.target.value)}
                              disabled={true}
                            />
                          </Form.Group>
                        </Col>

                        <Col md={3}>
                          <Form.Group>
                            <Form.Label>Πραγματογνώμονας</Form.Label>
                            <Form.Control
                              type="text"
                              value={
                                repair?.ExpertID
                                  ? `${repair.ExpertID} - ${repair.ExpertName}`
                                  : "Δεν έχει οριστεί"
                              }
                              disabled={true}
                            />
                          </Form.Group>
                        </Col>

                        <Col md={3}>
                          <Form.Group>
                            <Form.Label>Συνεργείο</Form.Label>
                            <Form.Control
                              type="text"
                              value={
                                repair?.GarageID
                                  ? `${repair.GarageID} - ${repair.GarageName}`
                                  : "Δεν έχει οριστεί"
                              }
                              disabled={true}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </div>

                {isNew && (
                  <div className="col-12 text-end">
                    <Button
                      variant="primary"
                      onClick={handleCreateRepair}
                      disabled={
                        (isSubmitting.status && isSubmitting.action === "create") ||
                        repair?.Insured?.Surname === "" ||
                        repair?.Insured?.Name === "" ||
                        repair?.Insured?.Mobile === "" ||
                        repair?.Insured?.Phone === "" ||
                        repair?.Insured?.TaxNumber === "" ||
                        repair?.Vehicle?.Make === "" ||
                        repair?.Vehicle?.Model === ""
                      }>
                      {isSubmitting.status && isSubmitting.action === "create" ? (
                        <>
                          <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                          <span className="ms-2">Δημιουργία...</span>
                        </>
                      ) : (
                        "Δημιουργία Επισκευής"
                      )}
                    </Button>
                  </div>
                )}

                {!isNew && (
                  <>
                    <div className="col-12" data-container-query>
                      <Card>
                        <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                          <div className="d-flex align-items-center">
                            <i className="fas fa-camera me-2" />
                            <h6 className="mb-0">Φωτογραφίες</h6>
                          </div>

                          <div className="d-flex gap-2">
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => handleOpenUploadModal()}
                              disabled={isNew || isSubmitting.status || !canUploadFiles}>
                              <i className="fas fa-upload me-2" />
                              Ανέβασμα Αρχείου
                            </Button>
                            {photoFiles.length > 0 && (
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleDownloadAll()}
                                disabled={isSubmitting.status}>
                                {isSubmitting.status && isSubmitting.action === "downloadAll" ? (
                                  <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    {isSubmitting.progress || "Λήψη Αρχείων..."}
                                  </>
                                ) : (
                                  <>
                                    <i className="fas fa-download me-2" />
                                    Λήψη Όλων
                                  </>
                                )}
                              </Button>
                            )}
                          </div>
                        </Card.Header>
                        <Card.Body>
                          {photoFiles.length > 0 ? (
                            <div className="d-flex flex-wrap gap-4 justify-content-center justify-content-container-md-start">
                              {photoFiles.map(file => (
                                <div
                                  key={file.FileID}
                                  className="position-relative"
                                  style={{
                                    width: "150px",
                                    cursor: "pointer"
                                  }}>
                                  <div
                                    className="file-preview-container border rounded p-2 text-center"
                                    onClick={() => handleFileDownload(file)}
                                    style={{
                                      height: "150px",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      backgroundColor: "#f8f9fa",
                                      transition: "all 0.2s ease-in-out"
                                    }}>
                                    {isSubmitting.status &&
                                    isSubmitting.action === "download" &&
                                    !isPreviewableFile(file.FileName) &&
                                    downloadingFileId === file.FileID ? (
                                      <div className="d-flex justify-content-center align-items-center h-100">
                                        <div className="spinner-border text-primary" role="status">
                                          <span className="visually-hidden">Φόρτωση...</span>
                                        </div>
                                      </div>
                                    ) : file.FileName.toLowerCase().match(/\.(jpg|jpeg|png|gif)$/) ? (
                                      <img
                                        src={`data:image/${file.FileName.split(".").pop()};base64,${
                                          file.FileByte
                                        }`}
                                        alt={file.FileName}
                                        style={{
                                          maxWidth: "100%",
                                          maxHeight: "100%",
                                          objectFit: "contain"
                                        }}
                                      />
                                    ) : file.FileName.toLowerCase().endsWith(".pdf") ? (
                                      <i className="fas fa-file-pdf fa-3x text-danger" />
                                    ) : file.FileName.toLowerCase().endsWith(".webm") ? (
                                      <i className="fas fa-file-video fa-3x text-primary" />
                                    ) : (
                                      <i className="fas fa-file fa-3x text-secondary" />
                                    )}
                                    <div
                                      className="file-actions position-absolute top-0 end-0 m-2 opacity-0"
                                      style={{ transition: "opacity 0.2s ease-in-out" }}>
                                      <ButtonGroup size="sm">
                                        <Button
                                          variant="light"
                                          className="shadow-sm"
                                          onClick={e => {
                                            e.stopPropagation()
                                            handleFileDownload(file)
                                          }}
                                          disabled={isSubmitting.status}>
                                          <i
                                            className={`fas ${
                                              isPreviewableFile(file.FileName) ? "fa-eye" : "fa-download"
                                            }`}
                                          />
                                        </Button>
                                        <Button
                                          variant="light"
                                          className="shadow-sm"
                                          onClick={e => {
                                            e.stopPropagation()
                                            handleDeleteFile(file)
                                          }}
                                          disabled={isSubmitting.status || !canDeleteFiles}>
                                          <i className="fas fa-trash text-danger" />
                                        </Button>
                                      </ButtonGroup>
                                    </div>
                                  </div>
                                  <div className="text-center mt-2">
                                    <small className="d-block text-truncate" style={{ maxWidth: "150px" }}>
                                      {file.FileName}
                                    </small>
                                    <small className="text-muted">
                                      {getAppLookupValue("FilesKind", file.FileKindID)}
                                    </small>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-4 text-muted">
                              <i className="fas fa-file fa-3x mb-3" />
                              <div>Δεν υπάρχουν διαθέσιμα αρχεία</div>
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    </div>

                    {repair?.Status >= 90 && (
                      <div className="col-12" data-container-query>
                        <Card>
                          <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <div className="d-flex align-items-center">
                              <i className="fas fa-camera me-2" />
                              <h6 className="mb-0">Φωτογραφίες Επισκευασμένου Οχήματος</h6>
                            </div>

                            <div className="d-flex gap-2">
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={() => handleOpenUploadModal([REPAIRED_VEHICLE_PHOTO_KIND_ID])}
                                disabled={isNew || isSubmitting.status || !canUploadRepairedFiles}>
                                <i className="fas fa-upload me-2" />
                                Ανέβασμα Αρχείου
                              </Button>
                              {repairedVehiclePhotoFiles.length > 0 && (
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() =>
                                    handleDownloadAll(repairedVehiclePhotoFiles, "downloadAllRepaired")
                                  }
                                  disabled={isSubmitting.status}>
                                  {isSubmitting.status && isSubmitting.action === "downloadAllRepaired" ? (
                                    <>
                                      <Spinner animation="border" size="sm" className="me-2" />
                                      {isSubmitting.progress || "Λήψη Αρχείων..."}
                                    </>
                                  ) : (
                                    <>
                                      <i className="fas fa-download me-2" />
                                      Λήψη Όλων
                                    </>
                                  )}
                                </Button>
                              )}
                            </div>
                          </Card.Header>
                          <Card.Body>
                            {repairedVehiclePhotoFiles.length > 0 ? (
                              <div className="d-flex flex-wrap gap-4 justify-content-center justify-content-container-md-start">
                                {repairedVehiclePhotoFiles.map(file => (
                                  <div
                                    key={file.FileID}
                                    className="position-relative"
                                    style={{
                                      width: "150px",
                                      cursor: "pointer"
                                    }}>
                                    <div
                                      className="file-preview-container border rounded p-2 text-center"
                                      onClick={() => handleFileDownload(file)}
                                      style={{
                                        height: "150px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        backgroundColor: "#f8f9fa",
                                        transition: "all 0.2s ease-in-out"
                                      }}>
                                      {isSubmitting.status &&
                                      isSubmitting.action === "download" &&
                                      !isPreviewableFile(file.FileName) &&
                                      downloadingFileId === file.FileID ? (
                                        <div className="d-flex justify-content-center align-items-center h-100">
                                          <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Φόρτωση...</span>
                                          </div>
                                        </div>
                                      ) : file.FileName.toLowerCase().match(/\.(jpg|jpeg|png|gif)$/) ? (
                                        <img
                                          src={`data:image/${file.FileName.split(".").pop()};base64,${
                                            file.FileByte
                                          }`}
                                          alt={file.FileName}
                                          style={{
                                            maxWidth: "100%",
                                            maxHeight: "100%",
                                            objectFit: "contain"
                                          }}
                                        />
                                      ) : file.FileName.toLowerCase().endsWith(".pdf") ? (
                                        <i className="fas fa-file-pdf fa-3x text-danger" />
                                      ) : file.FileName.toLowerCase().endsWith(".webm") ? (
                                        <i className="fas fa-file-video fa-3x text-primary" />
                                      ) : (
                                        <i className="fas fa-file fa-3x text-secondary" />
                                      )}
                                      <div
                                        className="file-actions position-absolute top-0 end-0 m-2 opacity-0"
                                        style={{ transition: "opacity 0.2s ease-in-out" }}>
                                        <ButtonGroup size="sm">
                                          <Button
                                            variant="light"
                                            className="shadow-sm"
                                            onClick={e => {
                                              e.stopPropagation()
                                              handleFileDownload(file)
                                            }}
                                            disabled={isSubmitting.status}>
                                            <i
                                              className={`fas ${
                                                isPreviewableFile(file.FileName) ? "fa-eye" : "fa-download"
                                              }`}
                                            />
                                          </Button>
                                          <Button
                                            variant="light"
                                            className="shadow-sm"
                                            onClick={e => {
                                              e.stopPropagation()
                                              handleDeleteFile(file)
                                            }}
                                            disabled={isSubmitting.status || !canDeleteRepairedFiles}>
                                            <i className="fas fa-trash text-danger" />
                                          </Button>
                                        </ButtonGroup>
                                      </div>
                                    </div>
                                    <div className="text-center mt-2">
                                      <small className="d-block text-truncate" style={{ maxWidth: "150px" }}>
                                        {file.FileName}
                                      </small>
                                      <small className="text-muted">
                                        {getAppLookupValue("FilesKind", file.FileKindID)}
                                      </small>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-center py-4 text-muted">
                                <i className="fas fa-file fa-3x mb-3" />
                                <div>Δεν υπάρχουν διαθέσιμα αρχεία</div>
                              </div>
                            )}
                          </Card.Body>
                        </Card>
                      </div>
                    )}

                    <div className="col-12">
                      <Card>
                        <Card.Header className="bg-navy text-white">
                          <h6 className="mb-0">Δικαιολογητικά Zημιάς</h6>
                        </Card.Header>
                        <Card.Body>
                          <Row className="d-flex justify-content-center gap-3">
                            <DocumentUploadCard
                              className="border-end"
                              title="Άδεια Κυκλοφορίας"
                              files={registrationDocuments}
                              onPreview={handleFileDownload}
                              onUpload={handleOpenUploadModal}
                              onDelete={handleDeleteFile}
                              isSubmitting={isSubmitting.status}
                              disabled={isNew || !canUploadRegistration}
                              deleteDisabled={!canDeleteRegistration}
                              fileKindIds={[REGISTRATION_DOCUMENT_KIND_ID]}
                            />
                            <DocumentUploadCard
                              className="border-end"
                              title="Οικονομική Προσφορά"
                              files={financialOffers}
                              onPreview={handleFileDownload}
                              onUpload={handleOpenUploadModal}
                              onDelete={handleDeleteFile}
                              isSubmitting={isSubmitting.status}
                              disabled={isNew || !canUploadFinancialOffer}
                              deleteDisabled={!canDeleteFinancialOffer}
                              fileKindIds={[FINANCIAL_OFFER_KIND_ID]}
                            />
                            <DocumentUploadCard
                              className="border-end"
                              title="Αίτηση Εκχώρησης"
                              files={assignmentRequests}
                              onPreview={handleFileDownload}
                              onUpload={handleOpenUploadModal}
                              onDelete={handleDeleteFile}
                              isSubmitting={isSubmitting.status}
                              disabled={isNew || !canUploadAssignmentRequest}
                              deleteDisabled={!canDeleteAssignmentRequest}
                              fileKindIds={[ASSIGNMENT_REQUEST_KIND_ID]}
                            />
                            <DocumentUploadCard
                              className="border-end"
                              title="Λοιπά δικαιολογητικά"
                              files={loipaDocuments}
                              onPreview={handleFileDownload}
                              onUpload={handleOpenUploadModal}
                              onDelete={handleDeleteFile}
                              isSubmitting={isSubmitting.status}
                              disabled={isNew || !canUploadOtherDocuments}
                              deleteDisabled={!canDeleteOtherDocuments}
                              fileKindIds={LOIPA_KIND_IDS}
                            />
                            <DocumentUploadCard
                              title="Τιμολόγια / Υπεύθυνη Δήλωση Μικροζημιάς (Άνευ Τιμολογίου)"
                              files={invoices}
                              onPreview={handleFileDownload}
                              onUpload={handleOpenUploadModal}
                              onDelete={handleDeleteFile}
                              isSubmitting={isSubmitting.status}
                              disabled={isNew || !canUploadInvoices}
                              deleteDisabled={!canDeleteInvoices}
                              uploadTooltip={
                                "Το τιμολόγιο μπορεί να μεταφορτωθεί μόνο από συνεργείο όταν η επισκευή βρίσκεται σε κατάσταση 'Επισκευή'"
                              }
                              fileKindIds={INVOICE_KIND_IDS}
                            />
                          </Row>
                        </Card.Body>
                      </Card>
                    </div>

                    <div className="col-12">
                      <Card>
                        <Card.Header className="bg-navy text-white">
                          <h6 className="mb-0">Μηνύματα</h6>
                        </Card.Header>
                        <Card.Body>
                          <div
                            className="chat-timeline mb-3"
                            style={{ maxHeight: "400px", overflowY: "auto" }}>
                            {(repair?.RepairMsgList?.MsgDetails || []).map(entry => (
                              <div key={entry.ID} className="mb-3">
                                <div className="d-flex justify-content-between">
                                  <small className="text-muted">
                                    {entry.UserFullname} - {formatDateForDisplay(entry.DateTimeStamp)}
                                  </small>
                                </div>
                                <p className="mb-0">{entry.Descr}</p>
                              </div>
                            ))}
                          </div>
                          <Form>
                            <Form.Group className="mb-2">
                              <Form.Control
                                as="textarea"
                                rows={3}
                                placeholder="Γράψτε το μήνυμά σας..."
                                value={newMessage}
                                onChange={e => setNewMessage(e.target.value)}
                                disabled={isSubmitting.status || repair?.Status === 100}
                              />
                            </Form.Group>
                            <div className="text-end">
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={handleSendMessage}
                                disabled={
                                  isSubmitting.status || !newMessage.trim() || repair?.Status === 100
                                }>
                                {isSubmitting.status && isSubmitting.action === "sendMessage" ? (
                                  <>
                                    <Spinner
                                      as="span"
                                      animation="border"
                                      size="sm"
                                      role="status"
                                      aria-hidden="true"
                                    />
                                    <span className="ms-2">Αποστολή...</span>
                                  </>
                                ) : (
                                  <>
                                    <i className="fas fa-paper-plane me-2" />
                                    Αποστολή
                                  </>
                                )}
                              </Button>
                            </div>
                          </Form>
                        </Card.Body>
                      </Card>
                    </div>
                  </>
                )}
              </div>
            </>
          ) : (
            <div>Δεν έχει επιλεγεί επισκευή.</div>
          )}

          <FileUploadModal
            show={showFileUpload}
            onHide={() => {
              setShowFileUpload(false)
              setUploadFileKindIds([])
            }}
            onUpload={handleFileUpload}
            fileKinds={lookupOptions.fileKind.filter(kind =>
              uploadFileKindIds?.length > 0
                ? uploadFileKindIds.includes(kind.Code)
                : ![
                    REGISTRATION_DOCUMENT_KIND_ID,
                    ...INVOICE_KIND_IDS,
                    FINANCIAL_OFFER_KIND_ID,
                    ASSIGNMENT_REQUEST_KIND_ID,
                    ...LOIPA_KIND_IDS,
                    REPAIRED_VEHICLE_PHOTO_KIND_ID
                  ].includes(kind.Code)
            )}
            fileKindIds={uploadFileKindIds}
          />

          {/* Add the container query styles */}
          <style>
            {`
            [data-container-query] {
              container-type: inline-size;
              container-name: responsive-container;
            }

            @container responsive-container (min-width: 768px) {
              .col-container-md-6 {
                width: 50%;
              }
              .col-container-md-3 {
                width: 25%;
              }
              .flex-container-md-row {
                flex-direction: row !important;
              }
              .justify-content-container-md-start {
                justify-content: flex-start !important;
              }
            }

            .bg-navy {
              background-color: #1f4e79 !important;
            }

            .table {
              background-color: transparent;
            }

            /* Ensure table cells and their content are visible */
            .table td {
              padding: 0.5rem;
              vertical-align: middle;
              background-color: transparent !important;
              color: #000000 !important;
            }

            /* Style form controls */
            .form-control, .form-select {
              background-color: #ffffff !important;
              border: 1px solid #ced4da !important;
              color: #212529 !important;
            }

            .form-control:disabled, .form-select:disabled {
              background-color: #e9ecef !important;
              opacity: 1;
            }
            
            .file-preview-container:hover .file-actions {
                opacity: 1 !important;
            }
          `}
          </style>
        </div>
      )}
    </Layout>
  )
}

export default RepairDetails
